import React, { useState, useEffect } from 'react';
import { Container, Row } from 'react-bootstrap';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Stack, TextField } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { updateGuestUserInfo } from '../../../../Utils/guestBookingStorage';
import { getDataFromLocalStorage } from '../../../../Service/getLocalStorage';

const GuestUserInfo = ({ 
  handleNext, 
  value, 
  setValue, 
  handlePrev, 
  setPolicy, 
  term_condition, 
  field 
}: any) => {
  const [error, setError] = useState<any>({});

  // Pre-fill form with logged-in user data
  useEffect(() => {
    const localData = getDataFromLocalStorage();
    if (localData?.id && localData?.is_email_verify != 0) {
      setValue({
        ...value,
        first_name: localData.first_name || value.first_name,
        last_name: localData.last_name || value.last_name,
        email: localData.email || value.email,
        phone: localData.phone || value.phone,
        due_date: localData.due_date ? new Date(localData.due_date) : value.due_date
      });
    }
  }, []);

  const handleChange = (e: any) => {
    const name = e.target.name;
    const val = e.target.value;
    
    setValue({
      ...value,
      [name]: val
    });

    // Update guest booking data
    updateGuestUserInfo({
      [name]: val
    });

    // Clear error when user starts typing
    if (error[name]) {
      setError({
        ...error,
        [name]: ""
      });
    }
  };

  const dateHandler = (value: Date | null) => {
    setValue((d: any) => ({
      ...d,
      due_date: value,
    }));
    
    // Update guest booking data - convert Date to string or undefined
    updateGuestUserInfo({
      due_date: value ? value.toISOString() : undefined
    });

    setError((errors: any) => ({
      ...errors,
      due_date: "",
    }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    
    const errors: any = {};
    
    // Validation
    if (!value.first_name || value.first_name.length < 2) {
      errors.first_name = "First name is required (minimum 2 characters)";
    }
    if (!value.last_name || value.last_name.length < 2) {
      errors.last_name = "Last name is required (minimum 2 characters)";
    }
    if (!value.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) {
      errors.email = "Valid email is required";
    }
    if (!value.phone || value.phone.length < 10) {
      errors.phone = "Valid phone number is required";
    }
    if (!value.due_date) {
      errors.due_date = "Due Date/Child's Birthday is required";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    // Save all user info to guest booking data
    updateGuestUserInfo(value);
    
    handleNext();
  };

  return (
    <div className="step-container step2">
      <Container>
        <Row>
          <div className="col-lg-12">
            <div className="consult-section-heading text-center">
              <h3>Complete Your Information</h3>
              <p className="my-4">Please provide your details to continue with the booking.</p>
            </div>
          </div>
        </Row>

        <form onSubmit={handleSubmit}>
          <Row>
            <div className="col-md-6 mb-3">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                name="first_name"
                className={`form-control ${error.first_name ? 'is-invalid' : ''}`}
                placeholder="Enter first name"
                value={value.first_name || ''}
                onChange={handleChange}
              />
              {error.first_name && <div className="invalid-feedback">{error.first_name}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                name="last_name"
                className={`form-control ${error.last_name ? 'is-invalid' : ''}`}
                placeholder="Enter last name"
                value={value.last_name || ''}
                onChange={handleChange}
              />
              {error.last_name && <div className="invalid-feedback">{error.last_name}</div>}
            </div>
          </Row>

          <Row>
            <div className="col-md-6 mb-3">
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                className={`form-control ${error.email ? 'is-invalid' : ''}`}
                placeholder="Enter email"
                value={value.email || ''}
                onChange={handleChange}
              />
              {error.email && <div className="invalid-feedback">{error.email}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Phone *</label>
              <input
                type="tel"
                name="phone"
                className={`form-control ${error.phone ? 'is-invalid' : ''}`}
                placeholder="Enter phone number"
                value={value.phone || ''}
                onChange={handleChange}
              />
              {error.phone && <div className="invalid-feedback">{error.phone}</div>}
            </div>
          </Row>

          <Row>
            <div className="col-md-6 mb-3">
              <label className="form-label">Due Date/Child's Birthday *</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack spacing={3}>
                  <MobileDatePicker
                    toolbarTitle="Choose date"
                    value={value?.due_date || null}
                    onChange={dateHandler}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        placeholder="MM-DD-YYYY"
                        className={`${error.due_date ? 'MuiInputBase-error-input' : ''}`}
                      />
                    )}
                  />
                </Stack>
              </LocalizationProvider>
              {error.due_date && <div className="text-danger small mt-1">{error.due_date}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Child's Name (Optional)</label>
              <input
                type="text"
                name="child_name"
                className="form-control"
                placeholder="Enter child's name"
                value={value.child_name || ''}
                onChange={handleChange}
              />
            </div>
          </Row>

          <Row>
            <div className="col-md-6 mb-3">
              <label className="form-label">Is this your first baby?</label>
              <select
                name="is_your_first_baby"
                className="form-control"
                value={value.is_your_first_baby || ''}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Partner/Support Person Name (Optional)</label>
              <input
                type="text"
                name="partner_support_person_name"
                className="form-control"
                placeholder="Enter partner/support person name"
                value={value.partner_support_person_name || ''}
                onChange={handleChange}
              />
            </div>
          </Row>

          <Row>
            <div className="col-md-6 mb-3">
              <label className="form-label">Partner/Support Person Email (Optional)</label>
              <input
                type="email"
                name="partner_support_person_email_address"
                className="form-control"
                placeholder="Enter partner/support person email"
                value={value.partner_support_person_email_address || ''}
                onChange={handleChange}
              />
            </div>
          </Row>

          <Row>
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms"
                  checked={term_condition}
                  onChange={(e) => setPolicy(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="terms">
                  I agree to the <a href="/client-services-agreement" target="_blank">Terms of Service</a> and <a href="/privacy-practices" target="_blank">Privacy Policy</a>
                </label>
              </div>
            </div>
          </Row>

          <Row>
            <div className="col-12 text-center">
              <button
                type="button"
                className="btn btn-secondary me-3"
                onClick={handlePrev}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn primary-blue-btn"
                disabled={!term_condition}
              >
                Continue
              </button>
            </div>
          </Row>
        </form>
      </Container>
    </div>
  );
};

export default GuestUserInfo;
